import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Divider,
  IconButton,
  TextField,
  Button,
  Chip,
  Skeleton,
  Tooltip,
} from '@mui/material';
import { ArrowLeft, Plus } from '@phosphor-icons/react';
import theme from '../../../theme';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import {
  getPromptVersionHistoryApi,
  useSystemPromptApi,
} from '../../../utils/apis';
import { useDashboard } from '../../../hooks/useDashboard';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import AddLabelToVersionHistoryModal from '../../modals/AddLabelToVersionHistoryModal';
import { useAlert } from '../../../hooks/useAlert';
import MessagePopup from '../../common/MessagePopup';
import EditVersionHistory from '../../modals/EditVersionHistory';
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import NoDataFallback from '../../common/NoDataFallback';

interface VersionHistoryCardsProps {
  onBack: () => void;
}

const VersionHistoryCards: React.FC<VersionHistoryCardsProps> = ({
  onBack,
}) => {
  const queryClient = useQueryClient();
  const { selectedAssistant } = useDashboard();
  const { alert, setAlert, showAlert } = useAlert();
  const [fromDate, setFromDate] = useState<Date | null>(() => {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth() - 1, today.getDate());
  });
  const [toDate, setToDate] = useState<Date | null>(new Date());
  const [promptVersionsHistory, setPromptVersionHistory] = useState<any>([]);
  const [isLabelModalOpen, setIsLabelModalOpen] = useState<boolean>(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [selectedVersionId, setSelectedVersionId] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedVersion, setSeletedVersion] = useState<any>({});
  const [updatingVersionId, setUpdatingVersionId] = useState<string | null>(
    null
  );

  const formatDate = (date: Date | null) =>
    date ? date.toISOString().split('T')[0] : '';

  const {
    data: promptData,
    isLoading: promptLoading,
    refetch: promptRefetch,
  } = useQuery({
    queryKey: [
      'get-version-history',
      selectedAssistant?._id,
      formatDate(fromDate),
      formatDate(toDate),
    ],
    queryFn: () =>
      getPromptVersionHistoryApi({
        agentId: selectedAssistant?._id || '',
        fromDate: formatDate(fromDate),
        toDate: formatDate(toDate),
      }),
    refetchOnMount: false,
    staleTime: 5 * 60 * 1000,
    enabled: !!selectedAssistant,
  });

  useEffect(() => {
    if (promptData) {
      setPromptVersionHistory(promptData?.data?.data);
    }
  }, [promptData]);

  useEffect(() => {
    promptRefetch();
  }, [toDate, fromDate, promptRefetch]);

  const closeLabelModal = () => {
    setIsLabelModalOpen(false);
  };

  const openLabelModal = (versionId: string) => {
    setSelectedVersionId(versionId);
    setIsLabelModalOpen(true);
  };
  const closeEditModal = () => {
    setIsEditModalOpen(false);
    setSeletedVersion({});
  };

  const openEditModal = (version: any) => {
    setSeletedVersion(version);
    setIsEditModalOpen(true);
  };

  const { mutateAsync: systemPromptAsync } = useMutation({
    mutationFn: useSystemPromptApi,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['get-version-history'] });
      showAlert('Prompt updated successfully', 'success');
      setUpdatingVersionId(null);
    },
    onError: (error: any) => {
      showAlert(
        error?.response?.data?.message || 'Failed to use prompt',
        'error'
      );
      setUpdatingVersionId(null);
    },
  });

  const handleUseSystemPrompt = (versionId: string | undefined) => {
    if (versionId) {
      setUpdatingVersionId(versionId);
      systemPromptAsync({
        versionId: versionId,
        agentId: selectedAssistant?._id || '',
      });
    }
  };

  const filteredVersions = searchQuery
    ? promptVersionsHistory?.filter((version: any) =>
        version.label?.some((label: string) =>
          label.toLowerCase().includes(searchQuery.toLowerCase())
        )
      )
    : promptVersionsHistory;

  if (promptLoading) {
    return Array.from({ length: 6 }).map((_, index) => (
      <Card
        key={index}
        sx={{
          boxShadow: 'none',
          height: '183px',
          padding: '16px',
          borderRadius: '6px',
          border: `1px solid ${theme.palette.lightGray.main}`,
        }}
      >
        <CardContent
          sx={{
            padding: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
          }}
        >
          <Skeleton variant="text" width="60%" height={24} />
          <Skeleton variant="text" width="80%" height={16} />
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Skeleton variant="rectangular" width={60} height={24} />
            <Skeleton variant="rectangular" width={60} height={24} />
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Skeleton variant="rectangular" width="100%" height={36} />
            <Skeleton variant="rectangular" width="100%" height={36} />
          </Box>
        </CardContent>
      </Card>
    ));
  }

  if (filteredVersions?.length === 0 && !promptLoading) {
    return (
      <Box sx={{ height: '100%' }}>
        <Box
          sx={{
            padding: '16px 20px',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <IconButton onClick={onBack}>
            <ArrowLeft
              size={20}
              color={theme.palette.custom.textBlue}
              weight="bold"
            />
          </IconButton>

          <Typography
            variant="h5"
            gutterBottom
            sx={{
              fontSize: '16px',
              fontWeight: 600,
              color: theme.palette.custom.textBlue,
              flexGrow: 1,
            }}
          >
            Version History
          </Typography>

          <TextField
            label="Search by label"
            variant="outlined"
            size="small"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            sx={{
              width: 300,
              '& .MuiInputBase-root': {
                height: '40px',
              },
            }}
          />

          <Box sx={{ display: 'flex', gap: 2 }}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DemoContainer components={['DatePicker']}>
                <DatePicker
                  label="From Date"
                  value={fromDate}
                  views={['year', 'month', 'day']}
                  onChange={setFromDate}
                  format="dd-MM-yyyy"
                  maxDate={new Date()}
                  slotProps={{
                    textField: {
                      size: 'small',
                      sx: {
                        width: 160,
                        '& .MuiInputBase-root': {
                          height: '40px',
                        },
                        '& input': {
                          fontSize: '14px',
                          color: theme.palette.lightGray.text,
                        },
                        '& .MuiSvgIcon-root': {
                          fontSize: '20px',
                        },
                      },
                    },
                  }}
                />

                <DatePicker
                  label="To Date"
                  value={toDate}
                  onChange={setToDate}
                  views={['year', 'month', 'day']}
                  format="dd-MM-yyyy"
                  minDate={fromDate || undefined}
                  slotProps={{
                    textField: {
                      size: 'small',
                      sx: {
                        width: 160,
                        '& .MuiInputBase-root': {
                          height: '40px',
                        },
                        '& input': {
                          fontSize: '14px',
                          color: theme.palette.lightGray.text,
                        },
                        '& .MuiSvgIcon-root': {
                          fontSize: '20px',
                        },
                      },
                    },
                  }}
                />
              </DemoContainer>
            </LocalizationProvider>
          </Box>
        </Box>
        <Divider />
        <NoDataFallback
          message="No Versions available"
          description="Looks like there are no versions available for the selected date range"
        />
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          padding: '16px 20px',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <IconButton onClick={onBack} sx={{ padding: '8px' }}>
          <ArrowLeft
            size={20}
            color={theme.palette.custom.textBlue}
            weight="bold"
          />
        </IconButton>

        <Typography
          variant="h5"
          sx={{
            fontSize: '16px',
            fontWeight: 600,
            color: theme.palette.custom.textBlue,
            flexGrow: 1,
            margin: 0,
          }}
        >
          Version History
        </Typography>

        <TextField
          label="Search by label"
          variant="outlined"
          size="small"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          sx={{
            width: 300,
            '& .MuiInputBase-root': {
              height: '40px',
            },
          }}
        />

        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <DatePicker
              label="From Date"
              value={fromDate}
              views={['year', 'month', 'day']}
              onChange={setFromDate}
              format="dd-MM-yyyy"
              maxDate={new Date()}
              slotProps={{
                textField: {
                  size: 'small',
                  sx: {
                    width: 160,
                    '& .MuiInputBase-root': {
                      height: '40px',
                    },
                    '& input': {
                      fontSize: '14px',
                      color: theme.palette.lightGray.text,
                    },
                    '& .MuiSvgIcon-root': {
                      fontSize: '20px',
                    },
                  },
                },
              }}
            />

            <DatePicker
              label="To Date"
              value={toDate}
              onChange={setToDate}
              views={['year', 'month', 'day']}
              format="dd-MM-yyyy"
              minDate={fromDate || undefined}
              slotProps={{
                textField: {
                  size: 'small',
                  sx: {
                    width: 160,
                    '& .MuiInputBase-root': {
                      height: '40px',
                    },
                    '& input': {
                      fontSize: '14px',
                      color: theme.palette.lightGray.text,
                    },
                    '& .MuiSvgIcon-root': {
                      fontSize: '20px',
                    },
                  },
                },
              }}
            />
          </Box>
        </LocalizationProvider>
      </Box>
      <Divider />
      <Box
        sx={{
          padding: '16px 20px',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(348px, 1fr))',
          gap: 2,
          flexGrow: 1,
          '&::-webkit-scrollbar': { display: 'none' },
          msOverflowStyle: 'none',
          scrollbarWidth: 'none',
          height: '100%',
          overflowY: 'auto',
          maxHeight: 'calc(100vh - 200px)',
        }}
      >
        {filteredVersions?.map((version: any) => (
          <Card
            key={version?._id}
            sx={{
              boxShadow: 'none',
              height: '183px',
              padding: '16px',
              borderRadius: '6px',
              border: `1px solid ${theme.palette.lightGray.main}`,
            }}
          >
            <CardContent
              sx={{
                padding: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 500,
                    fontSize: '16px',
                    color: theme.palette.custom.textBlue,
                  }}
                >
                  Version {version?.version}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.text.secondary,
                    fontSize: '12px',
                  }}
                >
                  Edited On {new Date(version.updatedAt).toLocaleString()}
                </Typography>
              </Box>
              {version.commitMessage ? (
                <Tooltip title={version.commitMessage}>
                  <Typography
                    sx={{
                      color: theme.palette.primary.main,
                      fontSize: '12px',
                      fontWeight: 400,
                      fontStyle: 'italic',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    "{version.commitMessage}"
                  </Typography>
                </Tooltip>
              ) : (
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.text.secondary,
                    fontSize: '12px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {version.identity}
                </Typography>
              )}

              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {version.label?.map((label: string, index: number) => (
                  <Chip
                    key={index}
                    label={label}
                    size="small"
                    sx={{
                      padding: '2px 12px',
                      border: `1px solid ${theme.palette.lightGray.main}`,
                      color: theme.palette.primary.main,
                      backgroundColor: theme.palette.custom.sidebarIconBg,
                    }}
                  />
                ))}
                <Button
                  variant="text"
                  size="small"
                  sx={{
                    textTransform: 'none',
                    color: theme.palette.text.secondary,
                    fontSize: '12px',
                  }}
                  onClick={() => openLabelModal(version._id)}
                >
                  <Plus size={12} />
                  Add Label
                </Button>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  fullWidth
                  sx={{
                    textTransform: 'none',
                    whiteSpace: 'nowrap',
                    boxShadow: 'none',
                  }}
                  onClick={() => openEditModal(version)}
                >
                  Edit
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  fullWidth
                  sx={{
                    textTransform: 'none',
                    textAlign: 'center',
                    borderRadius: '6px',
                    backgroundColor: '#F8F9FC',
                    color: theme.palette.lightGray.text,
                    fontSize: '14px',
                    border: `1px solid ${theme.palette.lightGray.main}`,
                    boxShadow: 'none',
                    fontWeight: 400,
                    '&:hover': {
                      boxShadow: 'none',
                    },
                  }}
                  onClick={() => handleUseSystemPrompt(version._id)}
                >
                  {updatingVersionId === version._id
                    ? 'Updating...'
                    : 'Use with Agent'}
                </Button>
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>
      <MessagePopup alert={alert} setAlert={setAlert} />
      <AddLabelToVersionHistoryModal
        open={isLabelModalOpen}
        onClose={closeLabelModal}
        versionId={selectedVersionId}
        selectedView="single"
      />
      <EditVersionHistory
        open={isEditModalOpen}
        onClose={closeEditModal}
        version={selectedVersion}
      />
    </Box>
  );
};

export default VersionHistoryCards;
